import useComposerSaved from '../../../hooks/useComposerSaved';
import React from 'react';
import { Box, ButtonBase, IconButton, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddPhotoAlternateOutlined as AddPhotoAlternateIcon,
  AddRounded as AddIcon,
  NotificationsActiveRounded as NotificationsActiveIcon,
  NotificationsNoneRounded as NotificationsNoneIcon,
  PhotoCameraOutlined as PhotoCameraIcon,
} from '@mui/icons-material';
import colors from '../../../assets/theme/colors';
import usePhotoCapture from '../../../hooks/usePhotoCapture';
import { DASHBOARD_QUICK_ENTRY_COPY } from './dashboardQuickEntryContent';
import DashboardInputCoachMark from './DashboardInputCoachMark';
import CareNoteComposerSurface from './CareNoteComposerSurface';
import { IMAGE_CAPTURE_INTENTS } from '../../../features/imageCapture/imageCaptureIntent';

export const DASHBOARD_INPUT_COACH_MARK_KEY = 'captureez:dashboard-input-coach-mark-dismissed:v1';

const QUICK_LOG_BUTTON_STYLES = {
  surface: {
    px: 1.6,
    py: 1.45,
    borderRadius: '18px',
    bgcolor: colors.semantic.bg,
    border: `1.5px solid ${alpha(colors.brand.tint, 0.58)}`,
    boxShadow: `0 10px 22px ${alpha(colors.brand.deep, 0.06)}`,
    transition: 'background-color 140ms ease, transform 140ms ease, box-shadow 140ms ease',
  },
  actionGrid: {
    display: 'flex',
    alignItems: 'stretch',
    gap: 0.75,
    mt: 0.75,
  },
};

const QUICK_LOG_FLOW_BORDER = `conic-gradient(from 0deg, ${alpha(colors.brand.ink, 0.5)}, ${alpha(colors.brand.deep, 0.42)}, ${alpha(colors.brand.tint, 0.46)}, ${alpha(colors.mood.happy, 0.32)}, ${alpha(colors.semantic.sage, 0.36)}, ${alpha(colors.brand.ink, 0.5)})`;
const QUICK_LOG_EMPTY_HINT = 'You can type, or use your phone keyboard mic to dictate a quick note.';

const DashboardQuickEntryCTA = ({
  child,
  onQuickEntry,
  onComposerSubmit = null,
  composerSaving = false,
  composerSavingLabel = 'Saving...',
  composerSavingHelper = 'Saving your note and organizing care details...',
  onImportLogs = null,
  onForwardEmail = null,
  onOpenSleepLog = null,
  onOpenFoodLog = null,
  onOpenMedicalLog = null,
  onOpenBathroomLog = null,
  onOpenIncident = null,
  compact = false,
  variant = 'default',
  captureDate = null,
  onCaptureDateChange = null,
  loggingReminderEnabled = false,
  loggingReminderLoading = false,
  onToggleLoggingReminder = null,
  dataCy = DASHBOARD_QUICK_ENTRY_COPY.dataCy,
  sx = {},
}) => {
  const [composerText, setComposerText] = React.useState('');
  const [composerPhotoFile, setComposerPhotoFile] = React.useState(null);
  const [composerPhotoIntent, setComposerPhotoIntent] = React.useState(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
  const [composerPhotoPreviewUrl, setComposerPhotoPreviewUrl] = React.useState('');
  const [composerFocused, setComposerFocused] = React.useState(false);
  const [coachMarkOpen, setCoachMarkOpen] = React.useState(false);
  const [coachMarkDismissed, setCoachMarkDismissed] = React.useState(false);
  const composerShellRef = React.useRef(null);
  const composerInputRef = React.useRef(null);
  const photoInputRef = React.useRef(null);
  const pendingPhotoIntentRef = React.useRef('');
  const {
    cameraOpen,
    cameraError,
    clearCameraError,
    videoRef,
    canvasRef,
    openCamera,
    closeCamera,
    handleFlipCamera,
    capturePhoto,
  } = usePhotoCapture({
    fileNamePrefix: 'log-update-photo',
    onCapture: (file) => {
      if (file) {
        setComposerPhotoFile(file);
        setComposerPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
      }
    },
    errorMessage: 'Could not access the camera. Try adding a photo instead.',
  });

  React.useEffect(() => {
    setComposerText('');
    setComposerPhotoFile(null);
    setComposerPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    setComposerFocused(false);
    closeCamera();
  }, [child?.id, closeCamera]);

  React.useEffect(() => {
    if (!composerPhotoFile) {
      setComposerPhotoPreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(composerPhotoFile);
    setComposerPhotoPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [composerPhotoFile]);

  const trimmedComposerText = composerText.trim();
  const hasComposerContent = Boolean(trimmedComposerText || composerPhotoFile);
  const composerActive = composerFocused || hasComposerContent || composerSaving || cameraOpen || Boolean(cameraError);

  React.useEffect(() => {
    if (variant !== 'composer' || !child?.id || composerActive || coachMarkDismissed) {
      return;
    }

    try {
      if (window.localStorage.getItem(DASHBOARD_INPUT_COACH_MARK_KEY) === 'true') {
        return;
      }
    } catch (error) {
      // If storage is blocked, still show the coach mark for this page view.
    }

    const frame = window.requestAnimationFrame(() => {
      setCoachMarkOpen(true);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [child?.id, coachMarkDismissed, composerActive, variant]);

  const dismissCoachMark = React.useCallback(() => {
    setCoachMarkDismissed(true);
    setCoachMarkOpen(false);
    try {
      window.localStorage.setItem(DASHBOARD_INPUT_COACH_MARK_KEY, 'true');
    } catch (error) {
      // Best-effort dismissal only; the UI still closes for this render.
    }
  }, []);

  React.useEffect(() => {
    if (!composerFocused || !composerInputRef.current) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      composerInputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [composerFocused]);

  const removeComposerPhoto = React.useCallback(() => {
    setComposerPhotoFile(null);
    setComposerPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    pendingPhotoIntentRef.current = '';
    setComposerPhotoPreviewUrl('');
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  }, []);

  useComposerSaved(child?.id, () => {
    setComposerText('');
    removeComposerPhoto();
    setComposerFocused(false);
  });

  if (!child) {
    return null;
  }

  const handleClick = (event) => {
    event?.stopPropagation?.();
    onQuickEntry?.(
      child,
      DASHBOARD_QUICK_ENTRY_COPY.actionKey,
      event,
      captureDate instanceof Date ? captureDate : null
    );
  };

  const handleReminderClick = (event) => {
    event?.stopPropagation?.();
    onToggleLoggingReminder?.(child);
  };

  const handleContinue = (event) => {
    event?.stopPropagation?.();
    if (!hasComposerContent || composerSaving) {
      return;
    }

    const resolvedCaptureDate = captureDate instanceof Date ? captureDate : null;
    if (typeof onComposerSubmit === 'function') {
      onComposerSubmit({
        actionKey: DASHBOARD_QUICK_ENTRY_COPY.actionKey,
        child,
        event,
        captureDate: resolvedCaptureDate,
        text: trimmedComposerText,
        photoFile: composerPhotoFile,
        photoIntent: composerPhotoIntent,
      });
      setComposerFocused(false);
      return;
    } else {
      onQuickEntry?.(
        child,
        DASHBOARD_QUICK_ENTRY_COPY.actionKey,
        event,
        resolvedCaptureDate,
        trimmedComposerText,
        null,
        composerPhotoFile,
        { photoIntent: composerPhotoIntent }
      );
    }
    setComposerText('');
    removeComposerPhoto();
    setComposerFocused(false);
  };

  const handleAddPhoto = (event) => {
    event?.stopPropagation?.();
    setComposerFocused(true);
    pendingPhotoIntentRef.current = IMAGE_CAPTURE_INTENTS.SAVE_PHOTO;
    setComposerPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    photoInputRef.current?.click();
  };

  const handlePhotoSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setComposerFocused(true);
    setComposerPhotoFile(file);
    setComposerPhotoIntent(pendingPhotoIntentRef.current || IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    pendingPhotoIntentRef.current = '';
    event.target.value = '';
  };

  const handleTakePhoto = (event) => {
    event?.stopPropagation?.();
    setComposerFocused(true);
    pendingPhotoIntentRef.current = IMAGE_CAPTURE_INTENTS.SAVE_PHOTO;
    setComposerPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    openCamera('environment', 'photo');
  };

  const handleUploadImage = (event) => {
    event?.stopPropagation?.();
    setComposerFocused(true);
    pendingPhotoIntentRef.current = IMAGE_CAPTURE_INTENTS.SAVE_PHOTO;
    setComposerPhotoIntent(IMAGE_CAPTURE_INTENTS.SAVE_PHOTO);
    photoInputRef.current?.click();
  };

  const handleUploadFile = (event) => {
    event?.stopPropagation?.();
    setComposerFocused(true);
    onImportLogs?.(child, event, {
      uploadKind: 'file',
      ...(captureDate instanceof Date ? { captureDate } : {}),
    });
  };

  const handleRecordVoice = (event) => {
    event?.stopPropagation?.();
    onQuickEntry?.(
      child,
      DASHBOARD_QUICK_ENTRY_COPY.actionKey,
      event,
      captureDate instanceof Date ? captureDate : null,
      '',
      'record_voice'
    );
  };

  const handleForwardEmail = (event) => {
    event?.stopPropagation?.();
    onForwardEmail?.(child, event);
  };

  const handleCloseCameraDialog = () => {
    closeCamera();
    clearCameraError();
  };

  const handleDismissComposer = (event) => {
    event?.stopPropagation?.();
    if (composerSaving) {
      return;
    }

    setComposerText('');
    removeComposerPhoto();
    setComposerFocused(false);
    closeCamera();
    clearCameraError();
  };

  if (variant === 'composer') {
    const resolvedCaptureDate = captureDate instanceof Date ? captureDate : new Date();

    if (!composerActive) {
      const shouldShowCoachMark = coachMarkOpen && !composerSaving;
      return (
        <>
          <Box sx={{ width: '100%' }}>
            <Paper
              ref={composerShellRef}
              elevation={0}
              data-cy={dataCy}
              aria-label={DASHBOARD_QUICK_ENTRY_COPY.ariaLabel}
              title={DASHBOARD_QUICK_ENTRY_COPY.title}
              sx={{
                ...sx,
                width: '100%',
                display: 'block',
                borderRadius: sx?.borderRadius || '999px',
                position: 'relative',
                overflow: 'hidden',
                isolation: 'isolate',
                p: '1.5px',
                bgcolor: 'transparent',
                zIndex: shouldShowCoachMark ? 1302 : 'auto',
                boxShadow: shouldShowCoachMark
                  ? `0 0 0 4px ${alpha(colors.semantic.profileFlow.completionText, 0.34)}, 0 0 0 9px ${alpha(colors.semantic.profileFlow.completionText, 0.16)}, 0 20px 44px ${alpha(colors.semantic.profileFlow.completionText, 0.28)}`
                  : `0 10px 24px ${alpha(colors.brand.tint, 0.12)}`,
                transition: 'box-shadow 180ms ease',
                '@keyframes quickLogFlowBorder': {
                  to: {
                    transform: 'rotate(1turn)',
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: '-75%',
                  zIndex: 0,
                  pointerEvents: 'none',
                  background: QUICK_LOG_FLOW_BORDER,
                  animation: 'quickLogFlowBorder 9s linear infinite',
                  filter: `drop-shadow(0 0 4px ${alpha(colors.brand.tint, 0.16)})`,
                  willChange: 'transform',
                },
                '@media (prefers-reduced-motion: reduce)': {
                  '&::before': {
                    animation: 'none',
                  },
                },
              }}
            >
              <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                overflow: 'hidden',
                borderRadius: sx?.borderRadius || '999px',
                bgcolor: colors.semantic.profileFlow.cardBg,
                border: `1px solid ${alpha(colors.brand.tint, shouldShowCoachMark ? 0.28 : 0.08)}`,
                px: { xs: 0.85, sm: 0.95 },
                py: 0.62,
                display: 'grid',
                gridTemplateColumns: '34px minmax(0, 1fr) auto',
                alignItems: 'center',
                columnGap: 0.64,
                ...sx,
              }}
            >
            <Box
              aria-hidden="true"
              sx={{
                width: 30,
                height: 30,
                borderRadius: '999px',
                display: 'grid',
                placeItems: 'center',
                color: colors.semantic.profileFlow.primary,
              }}
            >
              <AddIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box
              component="textarea"
              ref={composerInputRef}
              value={composerText}
              rows={1}
              onFocus={() => setComposerFocused(true)}
              onChange={(event) => {
                setComposerFocused(true);
                setComposerText(event.target.value);
              }}
              onPaste={() => setComposerFocused(true)}
              placeholder="Type or talk"
              aria-label="Type or talk"
              sx={{
                width: '100%',
                minWidth: 0,
                height: 34,
                resize: 'none',
                border: 0,
                outline: 0,
                p: 0,
                m: 0,
                bgcolor: 'transparent',
                color: colors.semantic.ink,
                fontSize: { xs: '0.96rem', sm: '1rem' },
                fontWeight: 400,
                lineHeight: '34px',
                overflow: 'hidden',
                '&::placeholder': {
                  color: alpha(colors.semantic.ink, 0.58),
                  opacity: 1,
                },
              }}
            />
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.38 }}>
              <IconButton
                onClick={handleAddPhoto}
                aria-label="Add photo to log update"
                title="Add photo"
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: alpha(colors.semantic.sageMist, 0.96),
                  color: colors.semantic.ink,
                  '&:hover': { bgcolor: colors.semantic.profileFlow.completionBg },
                }}
              >
                <AddPhotoAlternateIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                onClick={handleTakePhoto}
                aria-label="Take photo for log update"
                title="Take photo"
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: alpha(colors.semantic.sageMist, 0.96),
                  color: colors.semantic.ink,
                  '&:hover': { bgcolor: colors.semantic.profileFlow.completionBg },
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handlePhotoSelect}
            />
            </Box>
            </Paper>
            <Typography
              sx={{
                mt: 0.78,
                px: { xs: 1.25, sm: 1.8 },
                color: alpha(colors.semantic.ink, 0.68),
                fontSize: { xs: '0.79rem', sm: '0.88rem' },
                fontWeight: 500,
                lineHeight: 1.35,
                textAlign: 'left',
              }}
            >
              {QUICK_LOG_EMPTY_HINT}
            </Typography>
          </Box>
          <DashboardInputCoachMark
            open={shouldShowCoachMark}
            anchorRef={composerShellRef}
            onDismiss={dismissCoachMark}
          />
        </>
      );
    }

    return (
      <CareNoteComposerSurface
        child={child}
        dataCy={dataCy}
        sx={sx}
        captureDate={resolvedCaptureDate}
        onCaptureDateChange={onCaptureDateChange}
        composerInputRef={composerInputRef}
        composerText={composerText}
        onComposerFocus={() => setComposerFocused(true)}
        onComposerTextChange={(event) => setComposerText(event.target.value)}
        composerSaving={composerSaving}
        composerSavingLabel={composerSavingLabel}
        composerSavingHelper={composerSavingHelper}
        hasComposerContent={hasComposerContent}
        onContinue={handleContinue}
        onDismiss={handleDismissComposer}
        onOpenSleepLog={onOpenSleepLog}
        onOpenFoodLog={onOpenFoodLog}
        onOpenMedicalLog={onOpenMedicalLog}
        onOpenBathroomLog={onOpenBathroomLog}
        onOpenIncident={onOpenIncident}
        onTakePhoto={handleTakePhoto}
        onUploadImage={handleUploadImage}
        onUploadFile={handleUploadFile}
        onRecordVoice={handleRecordVoice}
        onForwardEmail={onForwardEmail ? handleForwardEmail : null}
        photoInputRef={photoInputRef}
        onPhotoSelect={handlePhotoSelect}
        composerPhotoFile={composerPhotoFile}
        composerPhotoPreviewUrl={composerPhotoPreviewUrl}
        onRemovePhoto={removeComposerPhoto}
        composerPhotoIntent={composerPhotoIntent}
        onComposerPhotoIntentChange={setComposerPhotoIntent}
        cameraOpen={cameraOpen}
        cameraError={cameraError}
        onCloseCamera={handleCloseCameraDialog}
        onFlipCamera={handleFlipCamera}
        videoRef={videoRef}
        onCapturePhoto={capturePhoto}
        canvasRef={canvasRef}
      />
    );

  }

  return (
    <Paper
      elevation={0}
      data-cy={dataCy}
      aria-label={DASHBOARD_QUICK_ENTRY_COPY.ariaLabel}
      title={DASHBOARD_QUICK_ENTRY_COPY.title}
      sx={{
        ...QUICK_LOG_BUTTON_STYLES.surface,
        ...sx,
      }}
    >
      <Typography
        sx={{
          fontSize: compact ? '0.72rem' : '0.78rem',
          fontWeight: 400,
          color: alpha(colors.semantic.ink, 0.78),
          lineHeight: 1.35,
          maxWidth: 520,
        }}
      >
        {DASHBOARD_QUICK_ENTRY_COPY.subtitle}
      </Typography>

      <Box sx={QUICK_LOG_BUTTON_STYLES.actionGrid}>
        <ButtonBase
          onClick={handleClick}
          aria-label={DASHBOARD_QUICK_ENTRY_COPY.ariaLabel}
          title={DASHBOARD_QUICK_ENTRY_COPY.quickActionLabel}
          sx={{
            width: '100%',
            minHeight: compact ? 42 : 48,
            px: 1.25,
            py: 0.8,
            borderRadius: '14px',
            bgcolor: alpha(colors.brand.deep, 0.16),
            color: colors.brand.deep,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            boxShadow: 'none',
            transition: 'transform 140ms ease, background-color 140ms ease',
            '&:hover': {
              bgcolor: alpha(colors.brand.deep, 0.22),
            },
            '&:active': {
              transform: 'scale(0.995)',
            },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '999px',
              bgcolor: alpha(colors.landing.surface, 0.5),
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <AddIcon sx={{ fontSize: 18, color: colors.brand.deep }} />
          </Box>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, lineHeight: 1.1 }}>
            {DASHBOARD_QUICK_ENTRY_COPY.quickActionLabel}
          </Typography>
        </ButtonBase>
      </Box>
      <Typography
        sx={{
          mt: 0.75,
          color: alpha(colors.semantic.ink, 0.62),
          fontSize: compact ? '0.7rem' : '0.74rem',
          fontWeight: 400,
          lineHeight: 1.35,
        }}
      >
        Use your phone keyboard mic to dictate a quick note.
      </Typography>
      {typeof onToggleLoggingReminder === 'function' ? (
        <ButtonBase
          onClick={handleReminderClick}
          disabled={loggingReminderLoading}
          aria-label={loggingReminderEnabled ? 'Turn off daily reminder' : 'Turn on daily reminder'}
          sx={{
            mt: 1,
            width: '100%',
            minHeight: 38,
            px: 1,
            py: 0.75,
            borderRadius: '13px',
            border: `1px solid ${alpha(colors.semantic.sage, loggingReminderEnabled ? 0.48 : 0.3)}`,
            bgcolor: loggingReminderEnabled
              ? alpha(colors.semantic.sage, 0.13)
              : alpha(colors.semantic.sageMist, 0.58),
            color: colors.semantic.ink,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            opacity: loggingReminderLoading ? 0.72 : 1,
            transition: 'background-color 140ms ease, border-color 140ms ease',
            '&:hover': {
              bgcolor: loggingReminderEnabled
                ? alpha(colors.semantic.sage, 0.18)
                : alpha(colors.semantic.sageMist, 0.82),
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            {loggingReminderEnabled ? (
              <NotificationsActiveIcon sx={{ fontSize: 18, color: colors.semantic.sageDeep, flexShrink: 0 }} />
            ) : (
              <NotificationsNoneIcon sx={{ fontSize: 18, color: alpha(colors.semantic.ink, 0.58), flexShrink: 0 }} />
            )}
            <Typography
              sx={{
                minWidth: 0,
                color: loggingReminderEnabled ? colors.semantic.sageDeep : alpha(colors.semantic.ink, 0.68),
                fontSize: compact ? '0.7rem' : '0.74rem',
                fontWeight: 750,
                lineHeight: 1.2,
                textAlign: 'left',
              }}
            >
              {loggingReminderEnabled ? 'Daily reminder on' : 'Daily reminder'}
            </Typography>
          </Box>
          <Typography
            sx={{
              color: loggingReminderEnabled ? colors.semantic.sageDeep : colors.brand.deep,
              fontSize: compact ? '0.68rem' : '0.72rem',
              fontWeight: 850,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {loggingReminderLoading ? 'Saving...' : loggingReminderEnabled ? 'Turn off' : 'Turn on'}
          </Typography>
        </ButtonBase>
      ) : null}
    </Paper>
  );
};

export default DashboardQuickEntryCTA;
